/* ============================================================
   FlowAvatar — FlowAI Voice-Avatar Komponente
   Dynaflow Technology GmbH
   ------------------------------------------------------------
   Das Dynaflow-Symbol als Chatbot-Avatar mit vier Zuständen.
   Kernidee: das Symbol ist ein Flow-Band — die Flussrichtung
   kodiert den Zustand:
     listening → Wellen fließen von außen INS Symbol (sky)
     speaking  → Wellen strahlen aus dem Symbol HERAUS (mint)
     thinking  → Energie zirkuliert (Segment-Sequenz + Orbit)
     idle      → ruhiges Atmen

   Verwendung:
     const avatar = new FlowAvatar(document.getElementById('el'), {
       size: 140,          // Symbolhöhe in px (optional)
       showStatus: true,   // Status-Zeile anzeigen (optional)
     });
     avatar.setState('listening');   // idle|listening|thinking|speaking
     avatar.setAmplitude(0.7);       // 0..1, echtes Audio-Level
     avatar.destroy();

   Ohne setAmplitude() simuliert die Komponente einen
   Sprech-/Hör-Rhythmus (Demo-Modus). Sobald setAmplitude()
   einmal aufgerufen wurde, wird nur noch der echte Wert genutzt.
   Keine Abhängigkeiten. Lizenz: intern, Dynaflow/WeShip.
   ============================================================ */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.FlowAvatar = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  const MINT = '#38EBBA', SKY = '#60D4FF', PALE = '#9FF5E1';

  const SYMBOL_PATHS = [
    'M19.0605 10.6104L5.37882 20.0015L2.49111 18.0185C0.931897 16.9496 0 15.1772 0 13.2826V2.69256C0 0.535287 2.40427 -0.741655 4.18252 0.468969L19.0592 10.6091L19.0605 10.6104Z',
    'M13.0219 33.5056L4.18252 39.5314C2.40427 40.7433 0 39.465 0 37.3091V26.719C0 24.8244 0.931897 23.0521 2.48981 21.9819L5.37752 20.0001L13.0349 25.2561C13.0349 25.2561 13.0401 25.2587 13.0427 25.2613C15.9291 27.2509 15.92 31.5316 13.0219 33.5042V33.5056Z',
    'M27.1968 23.8452L19.0586 29.3925L13.0214 33.5068C15.9194 31.5329 15.9285 27.2521 13.0421 25.2639C13.0395 25.2613 13.0369 25.2613 13.0343 25.2587L5.37695 20.0027L19.0586 10.6116L27.1955 16.1576C29.9005 18.0014 29.9005 22.0026 27.1955 23.8478L27.1968 23.8452Z'
  ];

  // Gradients: [id-suffix, attrs, stops]
  function gradientDefs(uid) {
    return `<defs>
      <linearGradient id="fa${uid}_g2" x1="10.8289" y1="4.37911" x2="1.47841" y2="17.5436" gradientUnits="userSpaceOnUse"><stop stop-color="#27B2BD"/><stop offset="0.89" stop-color="#38EBBA"/></linearGradient>
      <linearGradient id="fa${uid}_g1" x1="7.60163" y1="20.1822" x2="7.60163" y2="39.5171" gradientUnits="userSpaceOnUse"><stop offset="0.02" stop-color="#158F99"/><stop offset="0.89" stop-color="#60D4FF"/></linearGradient>
      <linearGradient id="fa${uid}_g0" x1="5.37695" y1="22.0585" x2="29.2252" y2="22.0585" gradientUnits="userSpaceOnUse"><stop stop-color="#27B2BD"/><stop offset="0.89" stop-color="#38EBBA"/></linearGradient>
    </defs>`;
  }

  const STATUS_DE = {
    idle: 'Flow ist bereit',
    listening: 'Flow hört zu …',
    thinking: 'Flow denkt nach …',
    speaking: 'Flow spricht'
  };

  let instanceCounter = 0;

  // CSS einmalig injizieren
  let styleInjected = false;
  function injectStyle() {
    if (styleInjected) return;
    styleInjected = true;
    const css = `
.flow-avatar{position:relative;display:flex;flex-direction:column;align-items:center;justify-content:center}
.flow-avatar__canvas{position:absolute;inset:0;width:100%;height:100%;pointer-events:none}
.flow-avatar__svg-wrap{position:relative;z-index:1;display:flex;align-items:center;justify-content:center}
.flow-avatar__svg-wrap svg{display:block;overflow:visible;transition:transform .5s cubic-bezier(.3,1.2,.3,1);will-change:transform,filter}
.flow-avatar .fa-seg{transform-box:fill-box;transform-origin:center;transition:filter .3s}
.flow-avatar[data-state="idle"] svg{animation:faBreathe 5s ease-in-out infinite}
@keyframes faBreathe{0%,100%{transform:scale(1)}50%{transform:scale(1.035)}}
.flow-avatar[data-state="listening"] svg{animation:faAttend 3.2s ease-in-out infinite;filter:drop-shadow(0 0 18px rgba(96,212,255,.28))}
@keyframes faAttend{0%,100%{transform:scale(.96) rotate(-3deg) translateY(0)}50%{transform:scale(.96) rotate(-3deg) translateY(-4px)}}
.flow-avatar[data-state="thinking"] .fa-seg{animation:faSegThink 1.6s cubic-bezier(.4,0,.2,1) infinite}
.flow-avatar[data-state="thinking"] .fa-seg-1{animation-delay:0s}
.flow-avatar[data-state="thinking"] .fa-seg-2{animation-delay:.26s}
.flow-avatar[data-state="thinking"] .fa-seg-3{animation-delay:.52s}
@keyframes faSegThink{0%,100%{filter:brightness(.75)}25%{filter:brightness(1.45)}55%{filter:brightness(.75)}}
.flow-avatar[data-state="speaking"] svg{filter:drop-shadow(0 0 22px rgba(56,235,186,.35))}
.flow-avatar__status{z-index:1;margin-top:18px;display:flex;align-items:center;gap:9px;font-size:13px;font-weight:600;letter-spacing:.03em;color:#5B6B82;min-height:20px;transition:color .4s;font-family:inherit}
.flow-avatar__status .fa-dot{width:6px;height:6px;border-radius:50%;background:#5B6B82;transition:background .4s,box-shadow .4s}
.flow-avatar[data-state="listening"] .flow-avatar__status{color:${SKY}}
.flow-avatar[data-state="listening"] .fa-dot{background:${SKY};box-shadow:0 0 9px ${SKY};animation:faDot 1.4s ease-in-out infinite}
.flow-avatar[data-state="thinking"] .flow-avatar__status{color:${PALE}}
.flow-avatar[data-state="thinking"] .fa-dot{background:${PALE};box-shadow:0 0 9px ${PALE};animation:faDot 1s ease-in-out infinite}
.flow-avatar[data-state="speaking"] .flow-avatar__status{color:${MINT}}
.flow-avatar[data-state="speaking"] .fa-dot{background:${MINT};box-shadow:0 0 9px ${MINT};animation:faDot .7s ease-in-out infinite}
@keyframes faDot{0%,100%{opacity:1}50%{opacity:.35}}
@media (prefers-reduced-motion: reduce){
  .flow-avatar svg,.flow-avatar .fa-seg,.flow-avatar .fa-dot{animation:none !important}
  .flow-avatar__canvas{display:none}
}`;
    const tag = document.createElement('style');
    tag.setAttribute('data-flow-avatar', '');
    tag.textContent = css;
    document.head.appendChild(tag);
  }

  class FlowAvatar {
    constructor(container, options = {}) {
      if (!container) throw new Error('FlowAvatar: container element required');
      injectStyle();

      this.uid = ++instanceCounter;
      this.opts = Object.assign({
        size: 140,                 // Symbolhöhe in px
        showStatus: true,
        statusTexts: STATUS_DE
      }, options);

      this.state = 'idle';
      this.amplitude = 0;
      this.simulate = true;
      this.rings = [];
      this.orbiters = [];
      this._nextRing = 0;
      this._sim = { env: 0, target: 0, nextShift: 0 };
      this._destroyed = false;
      this.reduced = typeof matchMedia !== 'undefined'
        && matchMedia('(prefers-reduced-motion: reduce)').matches;

      // ---- DOM ----
      this.root = document.createElement('div');
      this.root.className = 'flow-avatar';
      this.root.dataset.state = 'idle';

      this.canvas = document.createElement('canvas');
      this.canvas.className = 'flow-avatar__canvas';
      this.root.appendChild(this.canvas);

      const symH = this.opts.size;
      const symW = Math.round(symH * 33.3 / 44);
      const wrap = document.createElement('div');
      wrap.className = 'flow-avatar__svg-wrap';
      wrap.innerHTML = `<svg viewBox="-2 -2 33.3 44" width="${symW}" height="${symH}" fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="FlowAI">
        ${gradientDefs(this.uid)}
        <path class="fa-seg fa-seg-1" d="${SYMBOL_PATHS[0]}" fill="url(#fa${this.uid}_g2)"/>
        <path class="fa-seg fa-seg-2" d="${SYMBOL_PATHS[1]}" fill="url(#fa${this.uid}_g1)"/>
        <path class="fa-seg fa-seg-3" d="${SYMBOL_PATHS[2]}" fill="url(#fa${this.uid}_g0)"/>
      </svg>`;
      this.svg = wrap.firstElementChild;
      this.root.appendChild(wrap);

      if (this.opts.showStatus) {
        this.statusEl = document.createElement('div');
        this.statusEl.className = 'flow-avatar__status';
        this.statusEl.innerHTML = `<span class="fa-dot"></span><span class="fa-text"></span>`;
        this.statusText = this.statusEl.querySelector('.fa-text');
        this.statusText.textContent = this.opts.statusTexts.idle;
        this.root.appendChild(this.statusEl);
      }

      container.appendChild(this.root);

      // ---- Canvas-Sizing ----
      this.ctx = this.canvas.getContext('2d');
      this._resize = this._resize.bind(this);
      this._frame = this._frame.bind(this);
      this._ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(this._resize) : null;
      if (this._ro) this._ro.observe(this.root);
      this._resize();

      this._last = (typeof performance !== 'undefined' ? performance.now() : 0) / 1000;
      this._raf = requestAnimationFrame(this._frame);
    }

    /* ---------- Public API ---------- */
    setState(s) {
      if (!this.opts.statusTexts[s]) return;
      this.state = s;
      this.root.dataset.state = s;
      if (this.statusText) this.statusText.textContent = this.opts.statusTexts[s];
      this.rings.length = 0;
      this.orbiters.length = 0;
      if (s === 'thinking') this._initOrbiters();
      if (s !== 'speaking') this.svg.style.transform = '';
    }

    setAmplitude(v) {
      this.amplitude = Math.max(0, Math.min(1, v));
      this.simulate = false;
    }

    destroy() {
      this._destroyed = true;
      cancelAnimationFrame(this._raf);
      if (this._ro) this._ro.disconnect();
      this.root.remove();
    }

    /* ---------- intern ---------- */
    _resize() {
      const dpr = Math.min((typeof devicePixelRatio !== 'undefined' ? devicePixelRatio : 1), 2);
      const rect = this.root.getBoundingClientRect();
      this.W = rect.width; this.H = rect.height;
      this.canvas.width = this.W * dpr;
      this.canvas.height = this.H * dpr;
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Wellen-Zentrum = Mitte des Symbols (Status-Zeile unten ausgleichen)
      this.CX = this.W / 2;
      this.CY = this.H / 2 - (this.opts.showStatus ? 19 : 0);
      this.rIn = this.opts.size * 0.62;                      // Symbolkante
      this.rOut = Math.min(this.W, this.H) * 0.48;           // Außenradius
    }

    _initOrbiters() {
      const base = this.opts.size * 0.72;
      for (let i = 0; i < 7; i++) {
        this.orbiters.push({
          a: Math.random() * Math.PI * 2,
          r: base + Math.random() * base * 0.4,
          speed: 0.8 + Math.random() * 0.9,
          size: 1.4 + Math.random() * 1.8
        });
      }
    }

    _simAmplitude(t, dt) {
      if (!this.simulate) return;
      const s = this._sim;
      if (this.state === 'speaking') {
        if (t > s.nextShift) {
          s.target = Math.random() < 0.18 ? 0.05 : 0.45 + Math.random() * 0.55;
          s.nextShift = t + 0.12 + Math.random() * 0.22;
        }
        s.env += (s.target - s.env) * Math.min(1, dt * 14);
        this.amplitude = s.env * (0.75 + 0.25 * Math.sin(t * 34) * Math.sin(t * 21));
      } else if (this.state === 'listening') {
        if (t > s.nextShift) {
          s.target = Math.random() < 0.25 ? 0.08 : 0.3 + Math.random() * 0.5;
          s.nextShift = t + 0.25 + Math.random() * 0.5;
        }
        s.env += (s.target - s.env) * Math.min(1, dt * 7);
        this.amplitude = s.env;
      } else {
        this.amplitude *= Math.max(0, 1 - dt * 4);
      }
    }

    _spawnRings(t) {
      const amp = this.amplitude;
      if (this.state === 'speaking') {
        if (t > this._nextRing && amp > 0.12) {
          this.rings.push({ r: this.rIn, start: this.rIn, end: this.rOut, dir: 1, w: 1.5 + amp * 2.5, color: MINT });
          this._nextRing = t + 0.16 - amp * 0.06;
        }
      } else if (this.state === 'listening') {
        if (t > this._nextRing) {
          this.rings.push({ r: this.rOut, start: this.rOut, end: this.rIn, dir: -1, w: 1.4 + amp * 2, color: SKY });
          this._nextRing = t + 0.5 - amp * 0.18;
        }
      }
    }

    _drawRings(dt) {
      const ctx = this.ctx;
      for (const ring of this.rings) {
        const speed = (ring.dir > 0 ? 1.1 : 0.95) * this.rOut * (0.7 + this.amplitude * 0.6) * 0.45;
        ring.r += ring.dir * speed * dt * 3;
        const span = Math.abs(ring.end - ring.start);
        const p = Math.max(0, Math.min(1, Math.abs(ring.r - ring.start) / span));
        // outbound verblasst nach außen, inbound wird zum Symbol hin heller
        const alpha = ring.dir > 0 ? (1 - p) * 0.55 : 0.08 + p * 0.5;
        ctx.beginPath();
        ctx.arc(this.CX, this.CY, Math.max(4, ring.r), 0, Math.PI * 2);
        ctx.strokeStyle = ring.color;
        ctx.globalAlpha = alpha;
        ctx.lineWidth = ring.w;
        ctx.shadowColor = ring.color;
        ctx.shadowBlur = 12;
        ctx.stroke();
      }
      this.rings = this.rings.filter(r =>
        r.dir > 0 ? r.r < r.end : r.r > r.end);
    }

    _drawOrbiters(dt) {
      const ctx = this.ctx;
      for (const o of this.orbiters) {
        o.a += o.speed * dt;
        const x = this.CX + Math.cos(o.a) * o.r * 1.15;
        const y = this.CY + Math.sin(o.a) * o.r * 0.55;
        const front = Math.sin(o.a) > 0;
        ctx.beginPath();
        ctx.arc(x, y, o.size, 0, Math.PI * 2);
        ctx.fillStyle = PALE;
        ctx.globalAlpha = front ? 0.8 : 0.25;
        ctx.shadowColor = MINT;
        ctx.shadowBlur = front ? 10 : 3;
        ctx.fill();
      }
    }

    _applySpeakTransform() {
      if (this.state === 'speaking') {
        const s = 1 + this.amplitude * 0.085;
        this.svg.style.transform = `scale(${s}) translateY(${-this.amplitude * 5}px)`;
      }
    }

    _frame(now) {
      if (this._destroyed) return;
      const t = now / 1000;
      const dt = Math.min(t - this._last, 0.05);
      this._last = t;
      this.ctx.clearRect(0, 0, this.W, this.H);
      if (!this.reduced) {
        this._simAmplitude(t, dt);
        this._spawnRings(t);
        this._drawRings(dt);
        if (this.state === 'thinking') this._drawOrbiters(dt);
        this._applySpeakTransform();
        this.ctx.globalAlpha = 1;
        this.ctx.shadowBlur = 0;
      }
      this._raf = requestAnimationFrame(this._frame);
    }
  }

  return FlowAvatar;
});
